import React from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown, MoreVertical } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu';

export interface DataTableColumn<T> {
  key: string;
  title: string;
  sortable?: boolean;
  width?: string | number;
  render?: (value: any, record: T, index: number) => React.ReactNode;
  className?: string;
}

export interface DataTableAction<T> {
  key: string;
  label: string;
  icon?: React.ReactNode | ((record: T) => React.ReactNode);
  onClick: (record: T) => void;
  className?: string;
  disabled?: (record: T) => boolean;
  loading?: (record: T) => boolean; // New property to show loading state per record
  loadingIcon?: React.ReactNode; // Custom loading icon
  destructive?: boolean;
  inline?: boolean; // New property to show action as inline button instead of dropdown
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  loading?: boolean;
  actions?: DataTableAction<T>[];
  onSort?: (key: string, direction: 'asc' | 'desc') => void;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
  emptyMessage?: string;
  className?: string;
  rowKey: (record: T) => string | number;
  onRowClick?: (record: T) => void; // New property for row click handler
  rowClassName?: (record: T) => string; // New property for conditional row styling
}

export function DataTable<T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  actions = [],
  onSort,
  sortKey,
  sortDirection,
  emptyMessage,
  className,
  rowKey,
  onRowClick,
  rowClassName,
}: DataTableProps<T>) {
  const { t } = useTranslation(['shared', 'common']);
  const handleSort = (key: string) => {
    if (!onSort) return;

    const newDirection = sortKey === key && sortDirection === 'asc' ? 'desc' : 'asc';
    onSort(key, newDirection);
  };

  const getSortIcon = (key: string) => {
    if (!onSort) return null;

    if (sortKey === key) {
      return sortDirection === 'asc' ? (
        <ChevronUp className="ml-2 h-4 w-4" />
      ) : (
        <ChevronDown className="ml-2 h-4 w-4" />
      );
    }

    return <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />;
  };

  const renderCell = (column: DataTableColumn<T>, record: T, index: number) => {
    const value = record[column.key];

    if (column.render) {
      return column.render(value, record, index);
    }

    if (value === null || value === undefined) {
      return <span className="text-gray-400">-</span>;
    }

    if (typeof value === 'boolean') {
      return value ? t('common:boolean.yes') : t('common:boolean.no');
    }

    if (value instanceof Date) {
      return value.toLocaleDateString();
    }

    return String(value);
  };

  if (loading) {
    return (
      <div className={`bg-white border rounded-lg ${className}`}>
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500">{t('common:state.loading')}</p>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={`bg-white border rounded-lg ${className}`}>
        <div className="p-8 text-center">
          <p className="text-gray-500">{emptyMessage ?? t('table.noResults')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white border rounded-lg overflow-hidden ${className}`}>
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead
                key={column.key}
                style={column.width ? { width: column.width } : undefined}
                className={column.className}
              >
                {column.sortable && onSort ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="-ml-3 h-8 data-[state=open]:bg-accent"
                    onClick={() => handleSort(column.key)}
                  >
                    <span>{column.title}</span>
                    {getSortIcon(column.key)}
                  </Button>
                ) : (
                  <span>{column.title}</span>
                )}
              </TableHead>
            ))}
            {actions.length > 0 && (
              <TableHead className="text-right">{t('table.actions')}</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((record, index) => {
            const inlineActions = actions.filter((action) => action.inline);
            const dropdownActions = actions.filter((action) => !action.inline);

            return (
              <TableRow
                key={rowKey(record)}
                className={
                  rowClassName
                    ? rowClassName(record)
                    : onRowClick
                      ? 'cursor-pointer hover:bg-gray-50'
                      : ''
                }
                onClick={() => onRowClick?.(record)}
              >
                {columns.map((column) => (
                  <TableCell key={column.key} className={column.className}>
                    {renderCell(column, record, index)}
                  </TableCell>
                ))}
                {actions.length > 0 && (
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {/* Inline action buttons */}
                      {inlineActions.map((action) => {
                        const isDisabled = action.disabled?.(record) || false;
                        const isLoading = action.loading?.(record) || false;
                        const showAction = !isDisabled || isLoading; // Show button if loading even if normally disabled

                        if (!showAction) return null;

                        return (
                          <Button
                            key={action.key}
                            variant="ghost"
                            size="sm"
                            className={`h-8 w-8 p-0 ${action.destructive ? 'text-red-600 hover:text-red-700 hover:bg-red-50' : ''} ${action.className || ''}`}
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click when clicking action button
                              if (!isLoading) {
                                action.onClick(record);
                              }
                            }}
                            disabled={isDisabled || isLoading}
                            title={action.label}
                          >
                            {isLoading
                              ? action.loadingIcon || (
                                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                )
                              : typeof action.icon === 'function'
                                ? action.icon(record)
                                : action.icon}
                          </Button>
                        );
                      })}

                      {/* Dropdown menu for remaining actions */}
                      {dropdownActions.length > 0 && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={(e) => e.stopPropagation()} // Prevent row click
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {dropdownActions.map((action) => {
                              const isDisabled = action.disabled?.(record) || false;
                              return (
                                <DropdownMenuItem
                                  key={action.key}
                                  onSelect={() => {
                                    if (!isDisabled) {
                                      action.onClick(record);
                                    }
                                  }}
                                  disabled={isDisabled}
                                  className={`${action.destructive ? 'text-red-600 focus:text-red-600' : ''} ${action.className || ''}`}
                                >
                                  {typeof action.icon === 'function'
                                    ? action.icon(record)
                                    : action.icon}
                                  {action.label}
                                </DropdownMenuItem>
                              );
                            })}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default DataTable;
